/**
 * Persoenliche Todos-Listen-Page — Phase 2 von Todos-Notifications-Plan.
 *
 * URL: /todos
 *
 * Phase 2 = nur persoenliche Sicht. Senden an andere (Quittierung) +
 * Gruppen-Sichtbarkeit kommen in Phase 3.
 *
 * Dokument-Navigation (Abhakliste 2.91, 2026-10-01): Reiter ersetzend in der Adresse
 * (`tab`; `?mode=incoming|sent` aus Benachrichtigungen bleibt lesbar), Scroll-
 * Wiederherstellung, Seitentitel. Titel-Klick zeigt das Todo im Dokument-Dialog an, der
 * Stift öffnet ihn im Bearbeitungsmodus; Haken „erledigt" bleibt Sofort-Aktion (Ausnahme 1)
 * mit sichtbarer Rückmeldung; Ablehnen, Löschen und Fehler über Portal-Dialoge.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Check, Trash2, Calendar, AlertCircle, Loader2, Pencil, X, Inbox, Send, Bell } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';
import { DocumentDialog, param, useDialogs, useListState, usePageLabel, useScrollRestore } from '@onvice/portal-ui';

import TodoQuickCaptureModal from '../components/TodoQuickCaptureModal';

type TodoStatus = 'open' | 'in_progress' | 'done' | 'cancelled';
type AssignmentState = 'self' | 'pending' | 'accepted' | 'rejected';

interface Todo {
  id: string;
  title: string;
  description: string | null;
  status: TodoStatus;
  owner_user_id: string;
  assignee_user_id: string | null;
  assignment_state: AssignmentState;
  rejected_reason: string | null;
  due_at: string | null;
  reminder_at: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

type FilterKey = 'active' | 'today' | 'week' | 'done' | 'incoming' | 'sent';

const FILTERS: { key: FilterKey; label: string; query: string; icon?: typeof Inbox }[] = [
  { key: 'active',   label: 'Offen',       query: 'mode=mine&status=active' },
  { key: 'today',    label: 'Heute',       query: 'mode=mine&status=active&due=today' },
  { key: 'week',     label: 'Diese Woche', query: 'mode=mine&status=active&due=week' },
  { key: 'done',     label: 'Erledigt',    query: 'mode=mine&status=done' },
  { key: 'incoming', label: 'Eingehend',   query: 'mode=incoming', icon: Inbox },
  { key: 'sent',     label: 'Gesendet',    query: 'mode=sent', icon: Send },
];

/**
 * Listenzustand in der Adresse (R2): Reiter `tab`. `mode` ist die alte Adresse aus den
 * Benachrichtigungen (/todos?mode=incoming) — wird gelesen und beim nächsten Reiterwechsel
 * durch `tab` ersetzt.
 */
const TODOS_LIST = {
  tab: param.oneOf(['active', 'today', 'week', 'done', 'incoming', 'sent'] as const, 'active'),
  mode: param.string(),
};

/** Arbeitskopie im Dokument-Dialog — Zeiten als datetime-local-Text. */
interface TodoForm {
  title: string;
  description: string;
  due: string;
  reminder: string;
}

export default function TodosPage() {
  const { user } = useAuth();
  const myId = user?.id ?? '';
  const dlg = useDialogs();
  usePageLabel('Todos', 'list');
  const [list, setList] = useListState(TODOS_LIST);
  const filter: FilterKey = list.tab !== 'active'
    ? list.tab
    : list.mode === 'incoming' || list.mode === 'sent' ? list.mode : 'active';
  const setFilter = useCallback((f: FilterKey): void => { setList({ tab: f, mode: '' }); }, [setList]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [incomingCount, setIncomingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useScrollRestore(!loading);
  const [quickOpen, setQuickOpen] = useState(false);
  // Geöffnetes Todo im Dokument-Dialog: Titel-Klick zeigt an, Stift bearbeitet.
  const [opened, setOpened] = useState<{ id: string; startIn: 'read' | 'edit' } | null>(null);
  // Sofort-Aktion „erledigt": Rückmeldung je Zeile (Ausnahme 1, Mindeststandard).
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const q = FILTERS.find((f) => f.key === filter)?.query ?? '';
      const [list, cnt] = await Promise.all([
        api.get<{ success: boolean; todos: Todo[] }>(`/todos?${q}`),
        api.get<{ success: boolean; count: number }>(`/todos/incoming-count`),
      ]);
      setTodos(list.todos ?? []);
      setIncomingCount(cnt.count ?? 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  const accept = async (id: string): Promise<void> => {
    try {
      await api.post(`/todos/${id}/accept`);
      await load();
    } catch (err) {
      await dlg.error('Todo wurde nicht angenommen', err);
    }
  };

  const reject = async (id: string): Promise<void> => {
    const reason = await dlg.promptText({
      title: 'Todo ablehnen',
      message: 'Der Absender sieht den Grund in seiner Liste.',
      fieldLabel: 'Grund (optional)',
      allowEmpty: true,
      confirmLabel: 'Ablehnen',
    });
    if (reason === null) return;
    try {
      await api.post(`/todos/${id}/reject`, { reason });
      await load();
    } catch (err) {
      await dlg.error('Todo wurde nicht abgelehnt', err);
    }
  };

  // Einzel-Aktion (Ausnahme 1): schreibt sofort — mit sichtbarer Rückmeldung und bei einem
  // Fehler ohne Änderung der Anzeige.
  const toggle = async (id: string): Promise<void> => {
    if (busyId) return;
    setBusyId(id);
    try {
      const r = await api.post<{ success: boolean; todo: Todo }>(`/todos/${id}/toggle`);
      setTodos((prev) => prev.map((t) => t.id === id ? r.todo : t));
      setFlashId(id);
      window.setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 1500);
      // Wenn der aktuelle Filter den neuen Status nicht mehr matched, reload.
      if (filter !== 'done' && r.todo.status === 'done') void load();
      else if (filter === 'done' && r.todo.status !== 'done') void load();
    } catch (err) {
      await dlg.error('Todo wurde nicht geändert', err);
    } finally {
      setBusyId(null);
    }
  };

  const del = async (id: string): Promise<void> => {
    const ok = await dlg.confirm({ title: 'Todo löschen?', message: 'Das Todo wird endgültig entfernt.', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/todos/${id}`);
      setTodos((prev) => prev.filter((t) => t.id !== id));
      setOpened((cur) => (cur?.id === id ? null : cur));
    } catch (err) {
      await dlg.error('Todo wurde nicht gelöscht', err);
    }
  };

  const openedTodo = opened ? (todos.find((t) => t.id === opened.id) ?? null) : null;
  const openedBaseline: TodoForm | null = useMemo(() => openedTodo ? {
    title: openedTodo.title,
    description: openedTodo.description ?? '',
    due: openedTodo.due_at ? toLocalInputValue(openedTodo.due_at) : '',
    reminder: openedTodo.reminder_at ? toLocalInputValue(openedTodo.reminder_at) : '',
  } : null, [openedTodo]);

  const saveTodo = async (v: TodoForm): Promise<TodoForm> => {
    if (!opened) throw new Error('Kein Todo geöffnet');
    const r = await api.patch<{ success: boolean; todo: Todo }>(`/todos/${opened.id}`, {
      title: v.title.trim(),
      description: v.description.trim() || null,
      due_at: v.due ? new Date(v.due).toISOString() : null,
      reminder_at: v.reminder ? new Date(v.reminder).toISOString() : null,
    });
    setTodos((prev) => prev.map((x) => x.id === opened.id ? r.todo : x));
    return {
      title: r.todo.title,
      description: r.todo.description ?? '',
      due: r.todo.due_at ? toLocalInputValue(r.todo.due_at) : '',
      reminder: r.todo.reminder_at ? toLocalInputValue(r.todo.reminder_at) : '',
    };
  };

  const inputCls = 'w-full h-10 px-3 rounded-md border border-default bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-test-anchor="list" data-tab={filter}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Meine Todos</h1>
        <button
          type="button"
          onClick={() => setQuickOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} /> Neues Todo
        </button>
      </div>

      <div className="flex gap-1 mb-4 border-b border-default overflow-x-auto">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const isIncoming = f.key === 'incoming';
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 text-sm transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
                filter === f.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {Icon && <Icon size={14} />}
              {f.label}
              {isIncoming && incomingCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-danger text-white text-xs font-medium">
                  {incomingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm flex items-center gap-2" data-test-anchor="load-error">
          <AlertCircle size={16} /> {error}
          <button type="button" onClick={() => { void load(); }} className="ml-auto underline">Erneut laden</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> Lade...
        </div>
      ) : todos.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          Keine Todos in dieser Sicht.
        </div>
      ) : (
        <ul className="space-y-2">
          {todos.map((t) => {
            const isIncoming = filter === 'incoming';
            const isSent = filter === 'sent';
            const isEditable = !isIncoming && !isSent; // nur in „mine"-Tabs editieren
            const isMine = t.owner_user_id === myId;
            return (
            <li
              key={t.id}
              className="rounded-md border border-default bg-card p-3 flex items-start gap-3"
              data-todo-id={t.id}
            >
              {isIncoming ? (
                <Inbox size={20} className="mt-1 text-primary flex-shrink-0" />
              ) : isSent ? (
                <Send size={20} className="mt-1 text-muted-foreground flex-shrink-0" />
              ) : (
                <button
                  type="button"
                  onClick={() => void toggle(t.id)}
                  disabled={busyId === t.id}
                  title={t.status === 'done' ? 'Wieder öffnen' : 'Als erledigt markieren'}
                  data-action="toggle"
                  className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 disabled:opacity-60 ${
                    t.status === 'done'
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/40 hover:border-primary'
                  }`}
                >
                  {busyId === t.id ? <Loader2 size={12} className="animate-spin" /> : t.status === 'done' && <Check size={14} />}
                </button>
              )}

              {(
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => setOpened({ id: t.id, startIn: 'read' })}
                    className="text-left w-full"
                    data-action="open"
                  >
                    <div className={`font-medium ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                      {t.title}
                    </div>
                    {t.description && (
                      <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                        {t.description}
                      </div>
                    )}
                  </button>
                  {flashId === t.id && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-0.5" data-state="saved">
                      <Check size={12} /> {t.status === 'done' ? 'als erledigt gespeichert' : 'wieder geöffnet'}
                    </span>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {t.due_at && (
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        isOverdue(t) ? 'bg-danger/15 text-danger' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Calendar size={12} /> {formatDue(t.due_at)}
                      </span>
                    )}
                    {t.reminder_at && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                        <Bell size={12} /> {formatDue(t.reminder_at)}
                      </span>
                    )}
                    {isSent && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        t.assignment_state === 'pending'  ? 'bg-amber-500/15 text-amber-600'
                        : t.assignment_state === 'accepted' ? 'bg-emerald-500/15 text-emerald-600'
                        : t.assignment_state === 'rejected' ? 'bg-danger/15 text-danger'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                        {t.assignment_state === 'pending'  ? 'wartet' :
                         t.assignment_state === 'accepted' ? 'angenommen' :
                         t.assignment_state === 'rejected' ? 'abgelehnt' : t.assignment_state}
                      </span>
                    )}
                    {isSent && t.assignment_state === 'rejected' && t.rejected_reason && (
                      <span className="text-xs text-muted-foreground italic">„{t.rejected_reason}"</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-1 flex-shrink-0">
                {isIncoming && (
                  <>
                    <button
                      type="button"
                      onClick={() => void accept(t.id)}
                      title="Annehmen"
                      className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm flex items-center gap-1"
                    >
                      <Check size={14} /> Annehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => void reject(t.id)}
                      title="Ablehnen"
                      className="px-2.5 py-1 rounded-md border border-default text-muted-foreground hover:text-danger hover:border-danger text-sm flex items-center gap-1"
                    >
                      <X size={14} /> Ablehnen
                    </button>
                  </>
                )}
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => setOpened({ id: t.id, startIn: 'edit' })}
                    title="Bearbeiten"
                    data-action="edit"
                    className="p-1.5 text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {(isEditable || (isSent && isMine)) && (
                  <button
                    type="button"
                    onClick={() => void del(t.id)}
                    title="Löschen"
                    data-action="delete"
                    className="p-1.5 text-muted-foreground hover:text-danger min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}

      <TodoQuickCaptureModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreated={() => void load()}
      />

      {opened && openedTodo && (
        <DocumentDialog<TodoForm>
          open
          title={openedTodo.title}
          label="Todo"
          id={`todo:${opened.id}`}
          baseline={openedBaseline}
          startIn={opened.startIn}
          canEdit={filter !== 'incoming' && filter !== 'sent'}
          size="md"
          testAnchor="todo-dialog"
          validate={(v) => (v.title.trim() ? [] : ['Titel fehlt'])}
          onSave={saveTodo}
          onClose={() => setOpened(null)}
          renderRead={(v) => (
            <dl className="space-y-2 text-sm" data-test-anchor="todo-read">
              <div><dt className="text-muted-foreground">Titel</dt><dd className="font-medium text-base">{v.title}</dd></div>
              <div><dt className="text-muted-foreground">Beschreibung</dt><dd className="whitespace-pre-wrap">{v.description || '–'}</dd></div>
              <div><dt className="text-muted-foreground">Fällig</dt><dd>{openedTodo.due_at ? formatDue(openedTodo.due_at) : '–'}</dd></div>
              <div><dt className="text-muted-foreground">Erinnerung</dt><dd>{openedTodo.reminder_at ? formatDue(openedTodo.reminder_at) : '–'}</dd></div>
              <div><dt className="text-muted-foreground">Status</dt><dd>{openedTodo.status === 'done' ? 'erledigt' : openedTodo.status === 'cancelled' ? 'abgebrochen' : 'offen'}</dd></div>
            </dl>
          )}
          renderEdit={(v, setField) => (
            <div className="space-y-3">
              <input type="text" value={v.title} onChange={(e) => setField('title', e.target.value)} placeholder="Titel" autoFocus className={inputCls} data-test-anchor="todo-title" />
              <textarea
                value={v.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={3}
                placeholder="Beschreibung (optional)"
                className="w-full px-3 py-2 rounded-md border border-default bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Calendar size={12} /> Fällig</label>
                <input type="datetime-local" value={v.due} onChange={(e) => setField('due', e.target.value)} className={`${inputCls} sm:w-64`} />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Bell size={12} /> Erinnerung</label>
                <input type="datetime-local" value={v.reminder} onChange={(e) => setField('reminder', e.target.value)} className={`${inputCls} sm:w-64`} />
              </div>
            </div>
          )}
          readActions={(filter !== 'incoming' && filter !== 'sent') || openedTodo.owner_user_id === myId ? (
            <button
              type="button"
              onClick={() => void del(opened.id)}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg min-h-[44px] text-sm text-danger hover:bg-danger/10"
              data-action="delete"
            >
              <Trash2 size={14} /> Löschen
            </button>
          ) : undefined}
        />
      )}
    </div>
  );
}

function isOverdue(t: Todo): boolean {
  if (!t.due_at) return false;
  if (t.status === 'done' || t.status === 'cancelled') return false;
  return new Date(t.due_at).getTime() < Date.now();
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due.getTime() - today.getTime()) / (24 * 3600 * 1000));
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `heute ${time}`;
  if (diffDays === 1) return `morgen ${time}`;
  if (diffDays === -1) return `gestern ${time}`;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function toLocalInputValue(iso: string): string {
  // datetime-local-Input erwartet `YYYY-MM-DDTHH:mm` ohne Timezone.
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
